import React, { useEffect, useCallback } from "react";
import api from "../../common/lib/api";
import { Tag, Intent, InputGroup } from "@blueprintjs/core";
import { CompanyLinkChangeButton } from "../CompanyLinkChangeButton";

import updateLinkFor from "../BillPage/updateLinkFor";
import { shortenAtlasId } from "../shortenAtlasId";
import Managed, { useManaged } from "../../common/components/Managed";

const DEFAULT_STATE = {
  companies: [],
  accountingCompanies: {},
};

export default function Page() {
  const [state, setState, setError] = useManaged(DEFAULT_STATE);
  const params = new URLSearchParams(window.location.search);
  const query = (params.get("filter") || "").trim().replace(/\s+/g, " ");

  const load = useCallback(async () => {
    const { data } = await api.get("/companies-link");

    let { companies, accountingCompanies } = data;

    for (const company of companies) {
      company.billAccountingCompany = company.billAccountingCompanyId
        ? accountingCompanies[company.billAccountingCompanyId]
        : null;

      company.saleAccountingCompany = company.saleAccountingCompanyId
        ? accountingCompanies[company.saleAccountingCompanyId]
        : null;
    }

    function match(company, filter) {
      return (
        company &&
        (company.name.match(filter) || company.companyId.match(filter))
      );
    }

    if (query) {
      const queryRe = new RegExp(query.replace(/\s+/g, ".+"), "i");
      companies = companies.filter((company) => {
        if (
          match(company, queryRe) ||
          match(company.billAccountingCompany, queryRe) ||
          match(company.saleAccountingCompany, queryRe)
        ) {
          return true;
        }
        return false;
      });
    }

    return {
      companies,
      accountingCompanies,
    };
  }, [query]);

  const updateLink = updateLinkFor(
    () => load().then(setState, setError),
    setError
  );

  useEffect(() => {
    load().then(setState, setError);
  }, [load, setError, setState]);

  const { companies, accountingCompanies } = state.data;

  return (
    <>
      <Managed state={state}>
        <h1>Company-MYOB Links</h1>
        <p>
          Use this page to manage association between MYOB and Atlas accounts.
        </p>
        <div className="bp3-input-group print-hide">
          <form type="get">
            <InputGroup
              name="filter"
              leftIcon="filter"
              defaultValue={query || ""}
              placeholder="Please enter query and press enter"
            />
          </form>
        </div>
        <table className="bp3-html-table bp3-html-table-bordered bp3-html-table-condensed bp3-html-table-striped">
          <thead>
            <tr>
              <th>System</th>
              <th>Company ID</th>
              <th>Company Name</th>
              <th>Billing Account (in MYOB)</th>
              <th>Hardware Sales Account (in MYOB)</th>
              <th className="print-hide"></th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company) => {
              const { billAccountingCompany, saleAccountingCompany } = company;
              return (
                <LinkRow
                  key={company.companyId}
                  company={company}
                  billAccountingCompany={billAccountingCompany}
                  saleAccountingCompany={saleAccountingCompany}
                  accountingCompanies={accountingCompanies}
                  updateLink={updateLink}
                />
              );
            })}
          </tbody>
        </table>
      </Managed>
    </>
  );
}

function LinkRow({
  company,
  billAccountingCompany,
  saleAccountingCompany,
  accountingCompanies,
  updateLink,
}) {
  return (
    <tr className={billAccountingCompany ? "" : "print-hide"}>
      <td style={{ whiteSpace: "nowrap" }}>
        {company.system}
      </td>
      <td style={{ whiteSpace: "nowrap" }}>
        {shortenAtlasId(company.companyId)}
      </td>
      <td>
        {company.name}
        {company.billedSeparately ? (
          <>
            {" "}
            <Tag intent={Intent.SUCCESS}>Separately Billed</Tag>
          </>
        ) : (
          ""
        )}
      </td>
      <td>
        <AccountingCompany
          company={billAccountingCompany}
          linkedCompanyId={company.billAccountingCompanyId}
        />
      </td>
      <td>
        <AccountingCompany
          company={saleAccountingCompany}
          linkedCompanyId={company.saleAccountingCompanyId}
        />
      </td>
      <td className="print-hide">
        <CompanyLinkChangeButton
          company={company}
          accountingCompanies={accountingCompanies}
          updateLink={updateLink}
          system={company.system}
        />
      </td>
    </tr>
  );
}

function AccountingCompany({ company, linkedCompanyId }) {
  if (company) {
    return (
      <span title={company.companyId}>
        {`${company.name} (${company.companyId.split("::")[0]})`}
        {company.status !== "active" && (
          <>
            {" "}
            <span className="bp3-tag bp3-intent-danger">{company.status}</span>
          </>
        )}
      </span>
    );
  }
  if (linkedCompanyId) {
    return " TODO: MISSING ";
  }
  return "";
}
